import React from 'react';
import { useForm } from 'react-hook-form';
import { Modal, ModalActions, ModalBody } from 'components/ui/modal';
import Label from 'components/v2/forms/Label';
import TextField from 'components/v2/forms/TextField';
import Alert from 'components/ui/Alert';
import classNames from 'classnames';
import TextArea from 'components/v2/forms/TextArea';
import { useSendReport } from 'hooks/unifiedview/useReports';
import { t } from 'i18next';

const emailRegex = /\S+@\S+\.\S+/;
const TEXT_MIN_LENGTH = 3;

export default function EmailReportModal({ isOpen, onClose, payload }) {
  const { mutation } = useSendReport({
    onSuccess: handleSendReportSuccess,
    onError: handleSendReportFail,
  });

  function handleSendReportSuccess() {
    onClose();
  }

  function handleSendReportFail() {}

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const actionLabel = t('SEND');
  const title = t('EMAIL_REPORT');

  const primaryAction = {
    label: actionLabel,
    onClick: handleSubmit(handleSendReport),
  };

  const cancelLabel = t('CANCEL');

  const secondaryActions = [
    {
      label: cancelLabel,
      onClick: onClose,
    },
  ];

  const alertTitle = t('ADDRESS_FOLLOWING_ISSUES');

  function renderErrors() {
    const errorList = Object.values(errors);
    if (errorList.length === 0) {
      return;
    }

    return (
      (<Alert
        dataTestId="Alert-28c228ad"
        type="error"
        title={alertTitle}
        className="mb-4">
        <ul data-testid="ul-4e4d620f">
          {errorList.map((error, i) => (
            <li data-testid={`li-a19714be-${i}`} key={i}>{error.message}</li>
          ))}
        </ul>
      </Alert>)
    );
  }

  function validateEmails(value) {
    const isValid = value
      .split(',')
      .map((text) => text.trim())
      .every((email) => email && emailRegex.test(email));

    return isValid || t('INVALID_EMAILS');
  }

  async function handleSendReport(formFields) {
    const { subject, message, emails } = formFields;
    const { params: filterModel, columns, options } = payload;
    mutation.mutate({
      emails: emails.split(',').map((email) => email.trim()),
      subject,
      message,
      filterModel,
      columns,
      options,
    });
  }

  const subjectLabel = t('SUBJECT');
  const messageLabel = t('MESSAGE');
  const emailsLabel = t('EMAILS');

  function renderForm() {
    return (
      (<form data-testid="form-e6fc7366" onSubmit={handleSubmit(handleSendReport)}>
        <ModalBody dataTestId="ModalBody-15e0346d">
          {renderErrors()}
          <div
            data-testid="div-470d853f"
            className={classNames('mb-4', { error: !!errors.subject })}>
            <Label dataTestId="Label-45e47518" className="mb-1" htmlFor="subject">
              {subjectLabel} *
            </Label>
            <TextField
              dataTestId="TextField-bcd0efc1"
              passEventOnChange
              {...register('subject', {
                required: t('FIELD_IS_REQUIRED', { field: subjectLabel }),
                minLength: {
                  value: TEXT_MIN_LENGTH,
                  message: t('FIELD_MIN_LENGTH', {
                    field: subjectLabel,
                    minLength: TEXT_MIN_LENGTH,
                  }),
                },
              })} />
          </div>
          <div
            data-testid="div-dfb80ada"
            className={classNames('mb-4', { error: !!errors.message })}>
            <Label dataTestId="Label-497f0daa" className="mb-1">{messageLabel}</Label>
            <TextArea
              dataTestId="TextArea-209279b0"
              passEventOnChange
              error={!!errors.message}
              {...register('message', {
                minLength: {
                  value: TEXT_MIN_LENGTH,
                  message: t('FIELD_MIN_LENGTH', {
                    field: subjectLabel,
                    minLength: TEXT_MIN_LENGTH,
                  }),
                },
              })} />
          </div>
          <div
            data-testid="div-4db02025"
            className={classNames('mb-4', { error: !!errors.emails })}>
            <Label dataTestId="Label-101f34dc" className="mb-1">{emailsLabel} *</Label>
            <TextArea
              dataTestId="TextArea-8235da3f"
              passEventOnChange
              error={!!errors.description}
              placeholder={t('ENTER_EMAILS_SEPARATED_BY_COMMAS')}
              {...register('emails', {
                validate: validateEmails,
                required: t('FIELD_IS_REQUIRED', { field: emailsLabel }),
              })} />
          </div>
        </ModalBody>
        <ModalActions
          dataTestId="ModalActions-0fcf2263"
          primaryAction={primaryAction}
          secondaryActions={secondaryActions} />
      </form>)
    );
  }
  return (
    (<Modal
      dataTestId="Modal-c0ad6bc3"
      title={title}
      isOpen={isOpen}
      onClose={onClose}>
      {renderForm()}
    </Modal>)
  );
}
